import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Grid,
  Checkbox,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Pagination,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close,
  Search,
  CheckBox,
  CheckBoxOutlineBlank,
  Image,
  VideoFile,
  AudioFile,
  Article,
  Web,
  Clear,
  Add,
} from '@mui/icons-material';

import type { Media, Playlist, PlaylistItem } from '../../types';
import { MediaType, MediaStatus } from '../../types';
import apiClient from '../../services/api';
import { invalidatePlaylistData } from '../../utils/cacheInvalidation';

interface MediaSelectionDialogProps {
  open: boolean;
  playlist: Playlist | null;
  onClose: () => void;
  onMediaAdded: () => void;
}

interface MediaWithSelection extends Media {
  selected?: boolean;
}

const MediaSelectionDialog: React.FC<MediaSelectionDialogProps> = ({
  open,
  playlist,
  onClose,
  onMediaAdded,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [mediaType, setMediaType] = useState<string>('');
  const [selectedMedia, setSelectedMedia] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [adding, setAdding] = useState(false);

  // Загрузка медиафайлов
  const { data: mediaResponse, isLoading, refetch } = useQuery({
    queryKey: ['media', searchQuery, mediaType, currentPage],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        size: '12',
      });

      if (searchQuery.trim()) {
        params.append('query', searchQuery.trim());
      }

      if (mediaType) {
        params.append('media_type', mediaType);
      }

      const data = await apiClient.request('GET', `/media/?${params.toString()}`);
      return data;
    },
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setSelectedMedia(new Set());
      setSearchQuery('');
      setMediaType('');
      setCurrentPage(1);
    }
  }, [open]);

  // Обработка выбора медиафайла
  const handleMediaSelect = (mediaId: number) => {
    const newSelected = new Set(selectedMedia);
    if (newSelected.has(mediaId)) {
      newSelected.delete(mediaId);
    } else {
      newSelected.add(mediaId);
    }
    setSelectedMedia(newSelected);
  };

  // Выбрать все на странице
  const handleSelectAll = () => {
    if (!mediaResponse?.items) return;

    const allPageIds = mediaResponse.items.map((media: Media) => media.id);
    const newSelected = new Set(selectedMedia);

    const allSelected = allPageIds.every((id: number) => newSelected.has(id));

    if (allSelected) {
      // Снять выбор со всех на странице
      allPageIds.forEach((id: number) => newSelected.delete(id));
    } else {
      // Выбрать все на странице
      allPageIds.forEach((id: number) => newSelected.add(id));
    }

    setSelectedMedia(newSelected);
  };

  // Очистить выбор
  const handleClearSelection = () => {
    setSelectedMedia(new Set());
  };

  // Добавить выбранные медиафайлы в плейлист
  const handleAddToPlaylist = async () => {
    if (!playlist || selectedMedia.size === 0) return;

    setAdding(true);
    try {
      // Получаем текущее количество элементов для правильного order_index
                      const existingItemsResponse = await apiClient.request('GET', `/playlists/${playlist.id}/items`);
        const existingCount = existingItemsResponse.length;

      // Создаем массив элементов для добавления
      const itemsToAdd = Array.from(selectedMedia).map((mediaId, index) => ({
        media_id: mediaId,
        order_index: existingCount + index,
        transition_type: 'fade',
        transition_duration: 0.5,
        fit_mode: 'fill',
      }));

      // Массовое добавление элементов
              await apiClient.request('POST', `/playlists/${playlist.id}/items/bulk`, itemsToAdd);

      await invalidatePlaylistData(queryClient);
      onMediaAdded();
      onClose();
    } catch (error) {
      console.error('Ошибка добавления медиафайлов в плейлист:', error);
      // TODO: Показать уведомление об ошибке
    } finally {
      setAdding(false);
    }
  };

  // Получение иконки для типа медиа
  const getMediaIcon = (type: MediaType) => {
    switch (type) {
      case MediaType.IMAGE:
        return <Image color="primary" />;
      case MediaType.VIDEO:
        return <VideoFile color="secondary" />;
      case MediaType.AUDIO:
        return <AudioFile color="success" />;
      case MediaType.PDF:
        return <Article color="error" />;
      case MediaType.HTML:
        return <Web color="info" />;
      default:
        return <Article />;
    }
  };

  // Форматирование длительности
  const formatDuration = (duration: number) => {
    if (!duration) return '';
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Форматирование размера файла
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const selectedCount = selectedMedia.size;
  const totalPages = Math.ceil((mediaResponse?.total || 0) / 12);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {t('playlists.addMedia')}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Фильтры поиска */}
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('media.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('media.type')}</InputLabel>
                <Select
                  value={mediaType}
                  label={t('media.type')}
                  onChange={(e) => setMediaType(e.target.value)}
                >
                  <MenuItem value="">{t('common.all')}</MenuItem>
                  <MenuItem value="image">{t('media.types.image')}</MenuItem>
                  <MenuItem value="video">{t('media.types.video')}</MenuItem>
                  <MenuItem value="audio">{t('media.types.audio')}</MenuItem>
                  <MenuItem value="pdf">{t('media.types.pdf')}</MenuItem>
                  <MenuItem value="html">{t('media.types.html')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Box display="flex" gap={1}>
                <Button
                  size="small"
                  startIcon={<CheckBox />}
                  onClick={handleSelectAll}
                  disabled={!mediaResponse?.items?.length}
                >
                  {t('common.selectAll')}
                </Button>
                <Button
                  size="small"
                  startIcon={<Clear />}
                  onClick={handleClearSelection}
                  disabled={selectedCount === 0}
                >
                  {t('common.clear')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Информация о выборе */}
        {selectedCount > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('playlists.selectedMedia', { count: selectedCount })}
          </Alert>
        )}

        {/* Список медиафайлов */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : !mediaResponse?.items?.length ? (
          <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              {t('media.noMedia')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchQuery || mediaType
                ? t('media.noMediaFound')
                : t('media.noMediaDescription')
              }
            </Typography>
          </Paper>
        ) : (
          <List>
            {mediaResponse.items.map((media: Media) => (
              <ListItem
                key={media.id}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: selectedMedia.has(media.id) ? 'action.selected' : 'background.paper',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={selectedMedia.has(media.id)}
                    onChange={() => handleMediaSelect(media.id)}
                    icon={<CheckBoxOutlineBlank />}
                    checkedIcon={<CheckBox />}
                  />
                </ListItemIcon>

                <ListItemIcon>
                  {getMediaIcon(media.media_type)}
                </ListItemIcon>

                <ListItemText
                  primary={media.title || media.filename}
                  secondary={
                    <>
                      <Typography variant="body2" color="text.secondary">
                        {media.media_type} • {formatFileSize(media.file_size)}
                        {media.duration && ` • ${formatDuration(media.duration)}`}
                      </Typography>
                      {media.description && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {media.description}
                        </Typography>
                      )}
                    </>
                  }
                />

                <ListItemSecondaryAction>
                  <Chip
                    size="small"
                    label={t(`media.statuses.${media.status.toLowerCase()}`)}
                    color={media.status === MediaStatus.READY ? 'success' : 'default'}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}

        {/* Пагинация */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(event, page) => setCurrentPage(page)}
              color="primary"
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddToPlaylist}
          disabled={selectedCount === 0 || adding}
          loading={adding}
        >
          {adding
            ? t('playlists.adding')
            : t('playlists.addSelected', { count: selectedCount })
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MediaSelectionDialog;
